"use client";

import { useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Car,
  Wallet,
  Users,
  CalendarDays,
  FileText,
  ShieldCheck,
} from "lucide-react";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type FeatureAccent = "primary" | "emerald" | "violet" | "amber" | "sky" | "rose";

type FeatureCardData = {
  icon: React.ElementType;
  name: string;
  benefit: string;
  accent: FeatureAccent;
  featured?: boolean;
};

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------
const FEATURES: FeatureCardData[] = [
  {
    icon: Car,
    name: "Garagem",
    benefit: "Controle o ciclo de vida de cada veículo, da entrada à venda.",
    accent: "primary",
    featured: true,
  },
  {
    icon: Wallet,
    name: "Financeiro",
    benefit: "Veja a margem real de cada carro e o fluxo de caixa de todas as lojas.",
    accent: "emerald",
  },
  {
    icon: Users,
    name: "Clientes",
    benefit: "Histórico completo de cada comprador, com compras e trocas.",
    accent: "violet",
  },
  {
    icon: CalendarDays,
    name: "Agenda",
    benefit: "Organize contatos e visitas sem perder nenhum follow-up.",
    accent: "amber",
  },
  {
    icon: FileText,
    name: "Documentos",
    benefit: "Contratos e fichas gerados e armazenados com um clique.",
    accent: "sky",
  },
  {
    icon: ShieldCheck,
    name: "Equipe",
    benefit: "Permissões por cargo, acesso por loja, visibilidade total para o gestor.",
    accent: "rose",
  },
] as const;

// ---------------------------------------------------------------------------
// Static accent maps (Tailwind purge-safe — sem template literals dinâmicos)
// ---------------------------------------------------------------------------
const ACCENT_ICON_WRAP: Record<FeatureAccent, string> = {
  primary: "bg-primary/15 border-primary/30 text-primary",
  emerald: "bg-emerald-500/10 border-emerald-500/25 text-emerald-400",
  violet:  "bg-violet-500/10 border-violet-500/25 text-violet-400",
  amber:   "bg-amber-500/10 border-amber-500/25 text-amber-400",
  sky:     "bg-sky-500/10 border-sky-500/25 text-sky-400",
  rose:    "bg-rose-500/10 border-rose-500/25 text-rose-400",
};

const ACCENT_BORDER_HOVER: Record<FeatureAccent, string> = {
  primary: "hover:border-primary/50",
  emerald: "hover:border-emerald-500/40",
  violet:  "hover:border-violet-500/40",
  amber:   "hover:border-amber-500/40",
  sky:     "hover:border-sky-500/40",
  rose:    "hover:border-rose-500/40",
};

const ACCENT_GLOW: Record<FeatureAccent, string> = {
  primary: "rgba(58, 49, 216, 0.18)",
  emerald: "rgba(16, 185, 129, 0.12)",
  violet:  "rgba(139, 92, 246, 0.12)",
  amber:   "rgba(245, 158, 11, 0.12)",
  sky:     "rgba(14, 165, 233, 0.12)",
  rose:    "rgba(244, 63, 94, 0.12)",
};

const ACCENT_BADGE: Record<FeatureAccent, string> = {
  primary: "bg-primary/15 text-primary border-primary/30",
  emerald: "bg-emerald-500/10 text-emerald-400 border-emerald-500/25",
  violet:  "bg-violet-500/10 text-violet-400 border-violet-500/25",
  amber:   "bg-amber-500/10 text-amber-400 border-amber-500/25",
  sky:     "bg-sky-500/10 text-sky-400 border-sky-500/25",
  rose:    "bg-rose-500/10 text-rose-400 border-rose-500/25",
};

// ---------------------------------------------------------------------------
// Animation variants
// ---------------------------------------------------------------------------
const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
} as const;

const cardVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
  },
} as const;

// ---------------------------------------------------------------------------
// FeatureCard — spotlight segue o cursor
// ---------------------------------------------------------------------------
const FeatureCard = ({ card }: { card: FeatureCardData }) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [pointerPos, setPointerPos] = useState({ x: 0, y: 0 });
  const [hovered, setHovered] = useState(false);

  const Icon = card.icon;

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    setPointerPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  return (
    <motion.div
      ref={cardRef}
      variants={cardVariants}
      onPointerMove={handlePointerMove}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={cn(
        "group relative flex flex-col gap-5 rounded-2xl border border-border/50 bg-card/60 p-6 backdrop-blur-sm transition-all duration-300",
        ACCENT_BORDER_HOVER[card.accent],
        card.featured && "ring-1 ring-primary/20"
      )}
    >
      {/* Spotlight */}
      {hovered && (
        <div
          className="pointer-events-none absolute -inset-px rounded-[inherit] transition-opacity duration-300"
          style={{
            background: `radial-gradient(480px circle at ${pointerPos.x}px ${pointerPos.y}px, ${ACCENT_GLOW[card.accent]}, transparent 50%)`,
          }}
          aria-hidden
        />
      )}

      {/* Header row: icon + badge */}
      <div className="flex items-start justify-between gap-2">
        <div
          className={cn(
            "w-11 h-11 rounded-xl border flex items-center justify-center shrink-0 transition-transform duration-300 group-hover:scale-110",
            ACCENT_ICON_WRAP[card.accent]
          )}
        >
          <Icon className="w-5 h-5" aria-hidden />
        </div>

        {card.featured && (
          <span
            className={cn(
              "shrink-0 inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-widest",
              ACCENT_BADGE[card.accent]
            )}
          >
            Mais usado
          </span>
        )}
      </div>

      {/* Text */}
      <div className="flex flex-col gap-1.5">
        <h3 className="text-base font-bold text-foreground">{card.name}</h3>
        <p className="text-sm text-muted-foreground leading-relaxed">{card.benefit}</p>
      </div>
    </motion.div>
  );
};

// ---------------------------------------------------------------------------
// FeaturesSection
// ---------------------------------------------------------------------------
export default function FeaturesSection() {
  return (
    <section
      id="funcionalidades"
      className="relative z-10 py-24 bg-card/20 border-y border-border/40 overflow-hidden"
      aria-labelledby="features-headline"
    >
      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[60%] h-[50%] bg-primary/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-0 w-[30%] h-[40%] bg-primary/4 rounded-full blur-[100px]" />
      </div>

      <div className="max-w-6xl mx-auto px-6 relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-14"
        >
          <h2
            id="features-headline"
            className="text-3xl md:text-5xl font-black tracking-tight text-foreground mb-4"
          >
            Tudo que sua rede precisa,{" "}
            <span className="text-primary">integrado</span>
          </h2>
          <p className="text-base text-muted-foreground max-w-lg mx-auto">
            Módulos que se conversam em tempo real — sem planilha, sem exportação, sem retrabalho.
          </p>
        </motion.div>

        {/* Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
          role="list"
          aria-label="Módulos do NoWey"
        >
          {FEATURES.map((card) => (
            <div key={card.name} role="listitem">
              <FeatureCard card={card} />
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
